import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AddTemplateItemRequest,
  AssignTemplateToBusinessRequest,
  CreateComplianceTemplateRequest,
  FlexiComplianceBusinessTemplate,
  FlexiComplianceCatalogStructure,
  FlexiComplianceCatalogVersion,
  FlexiComplianceTemplate,
  FlexiComplianceTemplateItem,
  FlexiComplianceTemplateSection,
  FlexiComplianceTemplateSummary,
  UpdateComplianceTemplateRequest,
  UpdateTemplateItemRequest,
} from './flexi-compliance-template.models';

/**
 * Typed HTTP wrapper for the Flexi Compliance Templates admin API. Matches
 * the camelCase-tolerant mapper convention used by FlexiComplianceService;
 * server payloads may arrive as either casing (depending on JSON pipeline
 * config) so every reader falls back across both.
 */
@Injectable({ providedIn: 'root' })
export class FlexiComplianceTemplateService {
  private readonly base = `${environment.BASE_URL}/flexi/compliance/templates`;

  constructor(private http: HttpClient) {}

  // ---------- Library CRUD ----------

  /**
   * Lists templates. Pass `businessId` for any per-business surface (the
   * start-new-review pickers, the wizard's "start from existing" list): the
   * server then returns only this business's own templates plus unclaimed
   * library ones. Omitting it yields the full library — admin view only,
   * since an unscoped list shows every customer's named template.
   */
  list(includeDeleted = false, businessId?: number | null): Observable<FlexiComplianceTemplateSummary[]> {
    const scope = businessId ? `&businessId=${businessId}` : '';
    const url = `${this.base}?includeDeleted=${includeDeleted ? 'true' : 'false'}${scope}`;
    return this.http.get<any[]>(url).pipe(map(arr => (arr || []).map(r => this.mapSummary(r))));
  }

  get(templateId: number): Observable<FlexiComplianceTemplate> {
    return this.http.get<any>(`${this.base}/${templateId}`).pipe(map(r => this.mapTemplate(r)));
  }

  create(body: CreateComplianceTemplateRequest): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(this.base, body).pipe(map(r => this.mapTemplate(r)));
  }

  /**
   * Mutating calls accept an optional businessId for copy-on-write: when
   * the template is shared (2+ assignments) the backend forks it, moves
   * this business's assignment to the clone, and returns the CLONE — the
   * caller must follow the returned template's id.
   */
  update(templateId: number, body: UpdateComplianceTemplateRequest, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.put<any>(`${this.base}/${templateId}${this.bizQ(businessId)}`, body)
      .pipe(map(r => this.mapTemplate(r)));
  }

  remove(templateId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${templateId}`);
  }

  /** Deep copy (custom topics, items, exclusions) under a new name. No assignments. */
  duplicate(templateId: number, name?: string | null): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/${templateId}/duplicate`, { name: name ?? null })
      .pipe(map(r => this.mapTemplate(r)));
  }

  // ---------- Item management ----------

  addItem(templateId: number, body: AddTemplateItemRequest, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/${templateId}/items${this.bizQ(businessId)}`, body)
      .pipe(map(r => this.mapTemplate(r)));
  }

  updateItem(templateId: number, itemId: number, body: UpdateTemplateItemRequest, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.put<any>(`${this.base}/${templateId}/items/${itemId}${this.bizQ(businessId)}`, body)
      .pipe(map(r => this.mapTemplate(r)));
  }

  removeItem(templateId: number, itemId: number, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.delete<any>(`${this.base}/${templateId}/items/${itemId}${this.bizQ(businessId)}`)
      .pipe(map(r => this.mapTemplate(r)));
  }

  // ---------- Custom topics ----------

  addSection(templateId: number, title: string, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/${templateId}/sections${this.bizQ(businessId)}`, { title })
      .pipe(map(r => this.mapTemplate(r)));
  }

  renameSection(templateId: number, sectionId: number, title: string, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.put<any>(`${this.base}/${templateId}/sections/${sectionId}${this.bizQ(businessId)}`, { title })
      .pipe(map(r => this.mapTemplate(r)));
  }

  private bizQ(businessId?: number | null): string {
    return businessId ? `?businessId=${businessId}` : '';
  }

  // ---------- Per-business assignments ----------

  listAssignments(businessId: number): Observable<FlexiComplianceBusinessTemplate[]> {
    return this.http.get<any[]>(`${this.base}/businesses/${businessId}`)
      .pipe(map(arr => (arr || []).map(r => this.mapAssignment(r))));
  }

  assign(businessId: number, body: AssignTemplateToBusinessRequest): Observable<FlexiComplianceBusinessTemplate> {
    return this.http.post<any>(`${this.base}/businesses/${businessId}`, body).pipe(map(r => this.mapAssignment(r)));
  }

  unassign(businessId: number, templateId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/businesses/${businessId}/${templateId}`);
  }

  // ---------- Catalog (wizard picker data) ----------

  listCatalogVersions(): Observable<FlexiComplianceCatalogVersion[]> {
    return this.http.get<any[]>(`${this.base}/catalog-versions`)
      .pipe(map(arr => (arr || []).map(v => ({
        id: v.id ?? v.Id,
        code: v.code ?? v.Code ?? '',
        name: v.name ?? v.Name ?? '',
        effectiveFrom: v.effectiveFrom ?? v.EffectiveFrom,
        effectiveTo: v.effectiveTo ?? v.EffectiveTo ?? null,
        isCurrent: v.isCurrent ?? v.IsCurrent ?? false,
      }))));
  }

  getCatalogStructure(versionId: number): Observable<FlexiComplianceCatalogStructure> {
    return this.http.get<any>(`${this.base}/catalog-versions/${versionId}/structure`)
      .pipe(map(r => ({
        versionId: r.versionId ?? r.VersionId,
        versionCode: r.versionCode ?? r.VersionCode ?? '',
        versionName: r.versionName ?? r.VersionName ?? '',
        sections: ((r.sections ?? r.Sections) || []).map((s: any) => ({
          id: s.id ?? s.Id,
          position: s.position ?? s.Position ?? 0,
          title: s.title ?? s.Title ?? '',
          items: ((s.items ?? s.Items) || []).map((i: any) => ({
            id: i.id ?? i.Id,
            position: i.position ?? i.Position ?? 0,
            question: i.question ?? i.Question ?? '',
            regulatoryReference: i.regulatoryReference ?? i.RegulatoryReference ?? null,
            weight: i.weight ?? i.Weight ?? 1,
          })),
        })),
      })));
  }

  // ---------- Section exclusion ----------

  removeSection(templateId: number, sectionId: number, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/${templateId}/sections/${sectionId}/remove${this.bizQ(businessId)}`, {})
      .pipe(map(r => this.mapTemplate(r)));
  }

  restoreSection(templateId: number, sectionId: number, businessId?: number | null): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/${templateId}/sections/${sectionId}/restore${this.bizQ(businessId)}`, {})
      .pipe(map(r => this.mapTemplate(r)));
  }

  /**
   * Get-or-create the business's default template. Returns either the
   * existing default (if any) or a freshly-seeded one assigned as default.
   * Drives the "Edit template" entry point on the compliance rating page.
   */
  ensureDefaultForBusiness(businessId: number): Observable<FlexiComplianceTemplate> {
    return this.http.post<any>(`${this.base}/businesses/${businessId}/ensure-default`, {})
      .pipe(map(r => this.mapTemplate(r)));
  }

  // ---------- Mappers ----------

  private mapSummary = (r: any): FlexiComplianceTemplateSummary => ({
    id: r.id ?? r.Id,
    name: r.name ?? r.Name ?? '',
    description: r.description ?? r.Description ?? null,
    catalogVersionId: r.catalogVersionId ?? r.CatalogVersionId,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    catalogVersionName: r.catalogVersionName ?? r.CatalogVersionName ?? '',
    isActive: r.isActive ?? r.IsActive ?? true,
    isDeleted: r.isDeleted ?? r.IsDeleted ?? false,
    itemCount: r.itemCount ?? r.ItemCount ?? 0,
    businessAssignmentCount: r.businessAssignmentCount ?? r.BusinessAssignmentCount ?? 0,
    createdOn: r.createdOn ?? r.CreatedOn,
    modifiedOn: r.modifiedOn ?? r.ModifiedOn ?? null,
  });

  private mapTemplate = (r: any): FlexiComplianceTemplate => ({
    id: r.id ?? r.Id,
    name: r.name ?? r.Name ?? '',
    description: r.description ?? r.Description ?? null,
    catalogVersionId: r.catalogVersionId ?? r.CatalogVersionId,
    catalogVersionCode: r.catalogVersionCode ?? r.CatalogVersionCode ?? '',
    catalogVersionName: r.catalogVersionName ?? r.CatalogVersionName ?? '',
    isActive: r.isActive ?? r.IsActive ?? true,
    isDeleted: r.isDeleted ?? r.IsDeleted ?? false,
    businessAssignmentCount: r.businessAssignmentCount ?? r.BusinessAssignmentCount ?? 0,
    createdOn: r.createdOn ?? r.CreatedOn,
    modifiedOn: r.modifiedOn ?? r.ModifiedOn ?? null,
    sections: ((r.sections ?? r.Sections) || []).map((s: any) => this.mapSection(s)),
  });

  private mapSection = (s: any): FlexiComplianceTemplateSection => ({
    sectionId: s.sectionId ?? s.SectionId,
    position: s.position ?? s.Position ?? 0,
    title: s.title ?? s.Title ?? '',
    translateKey: s.translateKey ?? s.TranslateKey ?? null,
    isCustomTopic: s.isCustomTopic ?? s.IsCustomTopic ?? false,
    items: ((s.items ?? s.Items) || []).map((i: any) => this.mapItem(i)),
  });

  private mapItem = (i: any): FlexiComplianceTemplateItem => ({
    id: i.id ?? i.Id,
    position: i.position ?? i.Position ?? 0,
    sourceType: (i.sourceType ?? i.SourceType ?? 0) as 0 | 1,
    catalogItemId: i.catalogItemId ?? i.CatalogItemId ?? null,
    question: i.question ?? i.Question ?? '',
    regulatoryReference: i.regulatoryReference ?? i.RegulatoryReference ?? null,
    weight: i.weight ?? i.Weight ?? 1,
  });

  private mapAssignment = (a: any): FlexiComplianceBusinessTemplate => ({
    id: a.id ?? a.Id,
    businessId: a.businessId ?? a.BusinessId,
    templateId: a.templateId ?? a.TemplateId,
    templateName: a.templateName ?? a.TemplateName ?? '',
    catalogVersionCode: a.catalogVersionCode ?? a.CatalogVersionCode ?? '',
    isDefault: a.isDefault ?? a.IsDefault ?? false,
    assignedOn: a.assignedOn ?? a.AssignedOn,
    assignedBy: a.assignedBy ?? a.AssignedBy ?? null,
  });
}
